import React, { useEffect } from "react";
import {
  ArcMenuItem,
  ArcOutlinedInput,
  ArcOutlinedSelect,
  ArcStack,
  ArcText,
} from "@src/core/ui";
import { Controller } from "react-hook-form";
import { ArcGoogleSearch } from "@src/core/components";
import { selectMapLocation, setMapLocation } from "@src/core/controllers";
import { useDispatch, useSelector } from "react-redux";
import { REQUIRED_VALIDATION_TEXT } from "@src/core/config";
import { useChangeCoords } from "@src/core/hooks/useChangeCoords";
import { InputAdornment } from "@mui/material";
import { ZoneFormProps } from "./types";
import {
  selectZoneMapLocationUponCreation,
  setZoneMapLocationUponCreation,
} from '@src/features/zones/redux';
import { ZoneType } from "@src/core/features";
import { ZoneTypeName } from '@src/features/zones/config/zones-constants';

const MAX_RADIUS_M = 5000;

export const ZoneForm: React.FC<ZoneFormProps> = (props) => {
  const { control, setValue, register, formState: { errors }, watch } = props;

  const dispatch = useDispatch();
  const onChangeCoords = useChangeCoords();
  const location = useSelector(selectMapLocation);
  const createdZoneUponCreation = useSelector(selectZoneMapLocationUponCreation);

  useSelector(selectZoneMapLocationUponCreation);


  const updateZoneMapLocation = (
    lat: number,
    lng: number,
    zoneName: string,
    radius: number
  ) => {
    dispatch(
      setZoneMapLocationUponCreation({
        id: (watch("id") ?? 0),
        latitude: lat,
        longitude: lng,
        zoneName,
        radius,
        type: watch("zoneType") ?? ZoneType.TEMPORARY,
      })
    );
  };

  useEffect(() => {
    if (location) {
      if(location.address) setValue('address', location.address);
      setValue('latitude', String(location.latitude));
      setValue('longitude', String(location.longitude));

      if (createdZoneUponCreation) {
        dispatch(setZoneMapLocationUponCreation({...createdZoneUponCreation!, latitude: location.latitude, longitude: location.longitude}));
      }
    }
  }, [location]); 

  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between">
      <ArcStack flex={1}>
        <ArcStack marginTop={3} spacing={2}>

          <Controller
            control={control}
            name="zoneType"
            render={({ field: { value, onChange } }) => (
              <ArcOutlinedSelect
                label="Zone Type"
                value={value}
                onChange={(e) => {
                  onChange(e);
                  updateZoneMapLocation(
                    +(watch("latitude") ?? 0),
                    +(watch("longitude") ?? 0),
                    watch("zoneName") ?? "",
                    +(watch("zoneRadius") ?? 0 * 1000)
                  );
                }}
                error={Boolean(errors.zoneType)}
                helperText={errors.zoneType?.message}
              >
                {Object.entries(ZoneTypeName).map(([key, name]) => (
                  <ArcMenuItem key={key} value={key}>
                    {name}
                  </ArcMenuItem>
                ))}
              </ArcOutlinedSelect>
            )}
          />

          <Controller
            control={control}
            name="address"
            rules={{ required: "This field is required" }}
            render={({ field: { value } }) => (
              <ArcGoogleSearch
                initValue={value}
                label="Zone Location"
                error={Boolean(errors.address)}
                helperText={errors.address?.message}
                onSelect={(lat, lng, address) => {
                  setValue("address", address);
                  setValue("latitude", String(lat));
                  setValue("longitude", String(lng));

                  dispatch(setMapLocation({ latitude: lat, longitude: lng, address }));

                  updateZoneMapLocation(
                    lat,
                    lng,
                    watch("zoneName") ?? "",
                    +(watch("zoneRadius") ?? 0 * 1000)
                  );
                }}
              />
            )}
          />

          <ArcStack direction="row" spacing={2}>
            <Controller
              name="latitude"
              control={control}
              rules={{ required: REQUIRED_VALIDATION_TEXT }}
              render={({ field }) => (
                <ArcOutlinedInput
                  {...field}
                  label="Latitude"
                  autoComplete="off"
                  error={Boolean(errors.latitude)}
                  helperText={errors.latitude?.message}
                  onChange={(e) => {
                    const lat = Number(e.target.value);
                    setValue("latitude", String(lat));
                    onChangeCoords({ lat, lng: Number(watch("longitude") ?? 0) });
                    updateZoneMapLocation(
                      lat,
                      +(watch("longitude") ?? 0),
                      watch("zoneName") ?? "",
                      +(watch("zoneRadius") ?? 0 * 1000)
                    );
                  }}
                />
              )}
            />
            <Controller
              name="longitude"
              control={control}
              rules={{ required: REQUIRED_VALIDATION_TEXT }}
              render={({ field }) => (
                <ArcOutlinedInput
                  {...field}
                  label="Longitude"
                  autoComplete="off"
                  error={Boolean(errors.longitude)}
                  helperText={errors.longitude?.message}
                  onChange={(e) => {
                    const lng = Number(e.target.value);
                    setValue("longitude", String(lng));
                    onChangeCoords({ lat: Number(watch("latitude") ?? 0), lng });
                    updateZoneMapLocation(
                      +(watch("latitude") ?? 0),
                      lng,
                      watch("zoneName") ?? "",
                      +(watch("zoneRadius") ?? 0 * 1000)
                    );
                  }}
                />
              )}
            />
          </ArcStack>

          <Controller
            name="zoneName"
            control={control}
            rules={{
              required: "Zone name is required",
              maxLength: { value: 20, message: "Maximum 20 characters allowed" },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <ArcOutlinedInput
                autoComplete="off"
                label="Zone Name"
                value={value || ""}
                error={Boolean(error)}
                helperText={error?.message}
                maxLength={20}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange(val);
                  setValue("zoneName", val);
                  updateZoneMapLocation(
                    +(watch("latitude") ?? 0),
                    +(watch("longitude") ?? 0),
                    val,
                    +(watch("zoneRadius") ?? 0 * 1000)
                  );
                }}
              />
            )}
          />


          <Controller
            name="zoneDescription"
            control={control}
            rules={{
              maxLength: { value: 200, message: "Maximum 200 characters allowed" },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <ArcOutlinedInput
                autoComplete="off"
                label="Zone Description"
                value={value || ""}
                error={Boolean(error)}
                helperText={error?.message}
                maxLength={200}
                multiline
                onChange={onChange}
              />
            )}
          />

          <ArcOutlinedInput
            type="number"
            autoComplete="off"
            label="Zone Radius"
            error={
              Boolean(errors.zoneRadius) ||
    Number(watch("zoneRadius") ?? 0) > MAX_RADIUS_M
            }
            helperText={
              errors.zoneRadius?.message ||
    (Number(watch("zoneRadius") ?? 0) > MAX_RADIUS_M
      ? `Maximum radius is ${MAX_RADIUS_M} m`
      : "")
            }
            {...register("zoneRadius", {
              required: REQUIRED_VALIDATION_TEXT,
              validate: (value) =>
                Number(value) <= MAX_RADIUS_M || `Maximum radius is ${MAX_RADIUS_M} m`,
            })}
            onChange={(e) => {
              const value = e.target.value;
              const numericValue = Number(value);
              setValue("zoneRadius", Number(value), { shouldValidate: true });

              updateZoneMapLocation(
                +(watch("latitude") ?? 0),
                +(watch("longitude") ?? 0),
                watch("zoneName") ?? "",
                numericValue
              );
            }}
            value={watch("zoneRadius") ?? ""}
            inputProps={{
              step: 0.1, 
              min: 0,
            }}
            InputLabelProps={{ shrink: true }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <ArcText.h5>m</ArcText.h5>
                  </InputAdornment>
                ),
              },
            }}
          />

        </ArcStack>
      </ArcStack>
    </ArcStack>
  );
};
